import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type { FeatureStatusDto } from '@/lib/api';

export type OrgFeatureStatus = 'READY' | 'QUOTA_EXCEEDED' | 'DISABLED' | 'UNCONFIGURED';

export function featureStatusQueryKey(featureType: string): readonly unknown[] {
    return ['llm-features/status', featureType];
}

export async function fetchFeatureStatus(featureType: string): Promise<FeatureStatusDto> {
    const { data } = await api.llmFeatures.getStatus(featureType);
    return data;
}

interface UseOrgFeatureStatusResult {
    status: OrgFeatureStatus | undefined;
    reason: string | null | undefined;
    /** Feature is configured AND within quota — UI affordances should be enabled. */
    isReady: boolean;
    /** Feature is configured but the current period is exhausted — UI should disable + tooltip. */
    isQuotaExceeded: boolean;
    /** Feature is not configured or has been disabled — UI should hide the affordance entirely. */
    isUnavailable: boolean;
    isLoading: boolean;
    isError: boolean;
}

/**
 * Resolve whether an LLM-backed feature is currently usable for the caller's active
 * organization. Use to gate UI affordances (buttons, menu items) before triggering a
 * job that the runtime quota guard would otherwise reject.
 *
 * Mirrors the rule enforced by `QuotaService.reserveOrgPhase`. The four states map to
 * three rendering decisions that the caller usually wants:
 * - READY → render and enable
 * - QUOTA_EXCEEDED → render but disable, surface `reason` in a tooltip
 * - DISABLED / UNCONFIGURED → don't render the affordance at all
 */
export function useOrgFeatureStatus(featureType: string | null | undefined): UseOrgFeatureStatusResult {
    const query = useQuery({
        queryKey: featureStatusQueryKey(featureType ?? ''),
        queryFn: () => fetchFeatureStatus(featureType!),
        enabled: !!featureType,
        staleTime: 60_000,
    });

    const status = query.data?.status as OrgFeatureStatus | undefined;
    return {
        status,
        reason: query.data?.reason,
        isReady: status === 'READY',
        isQuotaExceeded: status === 'QUOTA_EXCEEDED',
        isUnavailable: status === 'DISABLED' || status === 'UNCONFIGURED',
        isLoading: query.isLoading,
        isError: query.isError,
    };
}
